// import DebugConsole from '@helper/functions/console';
import { invoiceAdminAPI, paymentAPI, publicExternalAPI, publicAPI, invoiceAPI } from './api';
import { handleErrorMessage } from './shared';
import { useAuthStore } from '@store/auth';

const isPaytoCashier = () => {
  const user = useAuthStore.getState().userAuthState;
  return !!(user?.isLoggedIn && user.Roles.includes('cashier'));
};

export type PublicPaytoCheckout = { request_id: string; public_access_token: string };

export const getPublicPaytoCheckout = (requestId: string): PublicPaytoCheckout | null => {
  const token = sessionStorage.getItem(`payto-checkout:${requestId}`);
  return token ? { request_id: requestId, public_access_token: token } : null;
};

const requirePublicPaytoCheckout = (requestId: string) => {
  const checkout = getPublicPaytoCheckout(requestId);
  if (!checkout) throw new Error('PayTo checkout has expired. Please start a new payment.');
  return checkout;
};

export async function getGenerateCaptureContext(
  healthEntityCode,
  paymentType,
  international = false,
) {
  try {
    let url = `/hppapi/payment/generate-capture-context`;

    if (international) {
      url = `/intapi/hppapi/payment/generate-capture-context`;
    }

    const { data } = await paymentAPI.get(url, {
      params: {
        healthEntityCode: healthEntityCode,
        paymentType: paymentType,
      },
    });
    return data.data;
  } catch (error) {
    handleErrorMessage(error);
    return false;
  }
}

export const getGenerateKey = async (healthEntityCode, paymentType) => {
  try {
    let url = `/hppapi/payment/generate-key`;

    const { data } = await paymentAPI.get(url, {
      params: {
        healthEntityCode: healthEntityCode,
        paymentType: paymentType,
      },
    });
    return data.data;
  } catch (error) {
    handleErrorMessage(error);
    return false;
  }
};

export const postPayToProcessPayment = async (body, international = false) => {
 try {
    if (!isPaytoCashier()) {
      const { data } = await invoiceAPI().post('/invapi/payment', {
        paymentMethod: body.paymentMethod, paymentType: body.paymentType, amount: body.amount,
        healthEntityCode: body.healthEntityCode, e2eId: body.e2eId,
        mandate_identification: body.mandate_identification,
        ...requirePublicPaytoCheckout(body.request_id),
      });
      return data;
    }
    let data;

    //replace , to whitespace on comments
    const updatedCommentsBulk =
      body?.activeInvoice?.terminalBulk?.comments?.replace(/,/g, ' ') || '';

    if (updatedCommentsBulk !== '') {
      body.activeInvoice.terminalBulk.comments = updatedCommentsBulk;
      body.activeInvoice.formData.comments = updatedCommentsBulk;
    }

    const updatedCommentsTerminal =
      body?.activeInvoice?.terminal?.comments?.replace(/,/g, ' ') || '';

    if (updatedCommentsTerminal !== '') {
      body.activeInvoice.terminal.comments = updatedCommentsTerminal;
    }
    console.log(body)

    console.log('CASHIER PAYMENT');
    let url = `/invapi/cashier/payment`;

    const response = await invoiceAdminAPI(body.healthEntityCode).post(url, body);
    data = response;

    return data.data;
  } catch (error) {
    handleErrorMessage(error);
    return false;
  }
};

export const postFlexProcessPayment = async (body, international = false) => {
  try {
    let data;

    //replace , to whitespace on comments
    const updatedCommentsBulk =
      body?.activeInvoice?.terminalBulk?.comments?.replace(/,/g, ' ') || '';

    if (updatedCommentsBulk !== '') {
      body.activeInvoice.terminalBulk.comments = updatedCommentsBulk;
      body.activeInvoice.formData.comments = updatedCommentsBulk;
    }

    const updatedCommentsTerminal =
      body?.activeInvoice?.terminal?.comments?.replace(/,/g, ' ') || '';

    if (updatedCommentsTerminal !== '') {
      body.activeInvoice.terminal.comments = updatedCommentsTerminal;
    }

    if (
      body.paymentMethod === 'cash' ||
      body.paymentMethod === 'cheque' ||
      body.paymentMethod === 'terminal'
    ) {
      console.log('CASHIER PAYMENT');
      let url = `/invapi/cashier/payment`;

      const response = await invoiceAdminAPI(body.healthEntityCode).post(url, body);
      data = response;
    } else {
      let url = `/hppapi/payment/process`;

      if (international) {
        url = `/intapi/hppapi/payment/process`;
      }

      const response = await paymentAPI.post(url, body);
      data = response.data;
    }
    return data.data;
  } catch (error) {
    handleErrorMessage(error);
    return false;
  }
};

/**
 * Calls backend to initiate Step 1: Setup Payer Auth
 * @see https://developer.cybersource.com/docs/cybs/en-us/payer-authentication/developer/all/rest/payer-auth/pa-ccdc-api-intro/pa-ccdc-setup-service-intro.html
 * @see /model/cybersourceservices/payment/helper/setupPayerAuthentication.js
 */
export const postSetupPayerAuth = async (body, international = false) => {
  try {
    let url = `/hppapi/payment/setup-payer-auth`;

    if (international) {
      url = `/intapi/hppapi/payment/setup-payer-auth`;
    }

    const { data }: { data: { data: SetupPayerAuthResponse } } = await paymentAPI.post(url, body);
    return data.data;
  } catch (error) {
    handleErrorMessage(error);
    return false;
  }
};

/**
 * Calls backend to initiate Step 3: Payer Authentication Check Enrollment Service
 * @see https://developer.cybersource.com/docs/cybs/en-us/payer-authentication/developer/all/rest/payer-auth/pa2-ccdc-enroll-intro.html
 * @see /model/cybersourceservices/payment/helper/checkPayerAuthenticationEnrollment.js
 */
export const postCheckPayerAuthEnrollment = async (
  body: ValidatePayerAuthEnrollmentParam,
  international = false,
) => {
  try {
    let url = `/hppapi/payment/check-payer-auth-enrollment`;

    if (international) {
      url = `/intapi/hppapi/payment/check-payer-auth-enrollment`;
    }

    const { data }: { data: { data: ValidatePayerAuthEnrollmentResponse } } = await paymentAPI.post(
      url,
      {...body, international},
    );
    return data.data;
  } catch (error) {
    handleErrorMessage(error);
    return false;
  }
};

export const postValidatePayerAuth = async (body, international = false) => {
  try {
    let url = `/hppapi/payment/validate-payer-auth`;

    if (international) {
      url = `/intapi/hppapi/payment/validate-payer-auth`;
    }
    const { data }: { data: { data: ValidateAuthResults } } = await paymentAPI.post(url, body);
    return data.data;
  } catch (error) {
    handleErrorMessage(error);
    return false;
  }
};

export const paytoCreateMandate = async (body, international = false, activeInvoice = undefined) => {
  try {
    if (!isPaytoCashier()) {
      if (!activeInvoice) throw new Error('Payment details are required.');
      const { data } = await invoiceAPI().post('/invapi/payto/mandates/payments/one-time', {
        ...body,
        public_payment_context: {
          reference: activeInvoice.merchantReference || activeInvoice.paymentType,
          referencePrefix: activeInvoice.referencePrefix || '',
          paymentType: activeInvoice.paymentType, paymentMethod: 'payto',
          amount: String(activeInvoice.amount), healthEntityCode: activeInvoice.healthEntityCode,
          activeInvoice: { ...activeInvoice, paymentMethod: 'payto' },
        },
      });
      if (!data.success || !data.public_access_token) throw new Error('Unable to create PayTo checkout.');
      sessionStorage.setItem(`payto-checkout:${body.request_id}`, data.public_access_token);
      return { success: data.success, data: data.data };
    }
    let url = `/invapi/cashier/payto/mandates/payments/one-time`;

    const { data } = await invoiceAdminAPI(body.healthEntityCode).post(url, body);
    console.log(data);
    return data;
  } catch (error) {
    handleErrorMessage(error);
    return false;
  }
};

export const paytoRecallMandate = async (mandateId, payload, healthEntityCode, international = false) => {
  try {
    if (!isPaytoCashier()) {
      const { data } = await invoiceAPI().patch(`/invapi/payto/mandates/actions/${encodeURIComponent(mandateId)}`,
        requirePublicPaytoCheckout(payload.request_id));
      return data;
    }
    
    let url = `/invapi/cashier/payto/mandates/actions/${mandateId}`;

    const { data } = await invoiceAdminAPI(healthEntityCode).patch(url, payload);
    console.log(data);
    return data;
  } catch (error) {
    handleErrorMessage(error);
    return false;
  }
};
export const paytoRequestMandate = async (uuid: string, healthEntityCode = "", international = false): Promise<any> => {
  try {
    if (!isPaytoCashier()) {
      const { data } = await invoiceAPI().post('/invapi/payto/mandates', requirePublicPaytoCheckout(uuid));
      return data;
    }
    const url = `/invapi/cashier/payto/mandates`;
    console.log("PRINT HEALTH ENTITY CODE");
    console.log(healthEntityCode);
    // Make a single API request
    const { data } = await invoiceAdminAPI(healthEntityCode).post(url, { request_id: uuid });

    console.log('Response from paytoRequestMandate:', data);
    return data; // Return the response data
  } catch (error) {
    console.error('Error in paytoRequestMandate:', error);
    throw error; // Propagate the error
  }
};

export const paytoRequestSingleMandate = async (indexField: string, value: string, international = false): Promise<any> => {
  try {
    const url = `/invapi/cashier/payto/mandate`;
    // Make a single API request
    const { data } = await invoiceAPI().post(url, {indexField: indexField, value: value});

    console.log('Response from paytoRequestMandate:', data);
    return data; // Return the response data
  } catch (error) {
    console.error('Error in paytoRequestMandate:', error);
    throw error; // Propagate the error
  }
};

export const paytoPostNotification = async (body: any, healthEntityCode, international = false, publicCheckout?: PublicPaytoCheckout) => {
  try {
    if (publicCheckout || !isPaytoCashier()) {
      const { data } = await invoiceAPI().post('/invapi/payto/notification/mandate', {
        ...body, ...(publicCheckout || requirePublicPaytoCheckout(body.request_id)),
      });
      return data;
    }
    const url = `/invapi/cashier/payto/notification/mandate`;

    // Make a single API request
    const { data } = await invoiceAdminAPI(healthEntityCode).post(url, body);

    console.log('Response from paytoPostWebhookResponse:', data);
    return data; // Return the response data
  } catch (error) {
    console.error('Error in paytoPostWebhookResponse:', error);
    throw error; // Propagate the error
  }
};

export const paytoCreateQueryNonProd = async (body: any, international = false) => {
  try {
    const url = `/invapi/payto/query/create`;  
    
    // Make a single API request using publicAPI
    const { data } = await invoiceAPI().post(url, body);

    console.log('Response from paytoCreateQuery:', data);
    return data; // Return the response data
  } catch (error) {
    console.error('Error in paytoCreateQuery:', error);
    throw error; // Propagate the error
  }
};

export const paytoRetrieveQuery = async (international = false) => {
  try {
    const url = `/invapi/payto/query`;  
    
    // Make a single API request using GET
    const { data } = await invoiceAPI().get(url);

    console.log('Response from paytoRetrieveQuery:', data);
    return data; // Return the response data
  } catch (error) {
    console.error('Error in paytoRetrieveQuery:', error);
    throw error; // Propagate the error
  }
};

export const paytoQueryResolution = async (body: any, healthEntityCode, international = false) => {
  try {
    console.log("PRINT HEALTH ENTITY CODE");
    console.log(healthEntityCode)
    const url = `/invapi/cashier/payto/query-resolution`;  
    
    // Make a single API request using GET
    const { data } = await invoiceAdminAPI(healthEntityCode).post(url, body);

    console.log('Response from paytoRetrieveQuery:', data);
    return data; // Return the response data
  } catch (error) {
    console.error('Error in paytoRetrieveQuery:', error);
    throw error; // Propagate the error
  }
};

